/**
 * Обёртка над Telegram WebApp SDK.
 *
 * Вне Telegram (обычный браузер при разработке) отдаёт заглушку, чтобы
 * витрину можно было открывать и тестировать без телефона. Заглушка ничего
 * не эмулирует «понарошку»: MainButton в этом режиме рисует само приложение,
 * см. компонент MainButton.
 */

interface TgMainButton {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  setText(t: string): void;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leave?: boolean): void;
  hideProgress(): void;
  setParams(p: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface TgBackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface TgHaptic {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name?: string; last_name?: string; username?: string };
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  viewportStableHeight: number;
  isExpanded: boolean;
  MainButton: TgMainButton;
  BackButton: TgBackButton;
  HapticFeedback: TgHaptic;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  openTelegramLink(url: string): void;
  requestContact?(cb: (ok: boolean, result?: { responseUnsafe?: { contact?: { phone_number?: string } } }) => void): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

const real = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

/** true, когда приложение реально открыто внутри Telegram. */
export const isTelegram = Boolean(real && real.initData !== undefined && real.platform !== 'unknown');

export const tg = real;

/** Инициализация. Вызывается один раз при старте приложения. */
export function initTelegram(): void {
  if (!real) return;
  real.ready();
  real.expand();
  // Панель Telegram становится продолжением тёмной шапки приложения
  try {
    real.setHeaderColor('#111820');
    real.setBackgroundColor('#EEF0EF');
    real.setBottomBarColor?.('#EEF0EF');
  } catch {
    /* старые клиенты не поддерживают — не критично */
  }
  const applyViewport = () => {
    if (real.viewportStableHeight) {
      document.documentElement.style.setProperty('--tg-viewport', `${real.viewportStableHeight}px`);
    }
  };
  applyViewport();
  real.onEvent('viewportChanged', applyViewport);
}

export const haptic = {
  light: () => real?.HapticFeedback?.impactOccurred('light'),
  medium: () => real?.HapticFeedback?.impactOccurred('medium'),
  success: () => real?.HapticFeedback?.notificationOccurred('success'),
  error: () => real?.HapticFeedback?.notificationOccurred('error'),
  select: () => real?.HapticFeedback?.selectionChanged(),
};

/** Payload из deep link: t.me/bot/app?startapp=p_deeper-pro-plus-2 */
export function startParam(): string | null {
  return real?.initDataUnsafe?.start_param ?? null;
}

export function initDataHeader(): string | null {
  return real?.initData && real.initData.length > 0 ? real.initData : null;
}

/** Открыть чат с менеджером. Вне Telegram — обычная вкладка. */
export function openManagerChat(username: string, text?: string): void {
  const url = `https://t.me/${username}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
  if (real?.openTelegramLink) real.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

/**
 * Телефон из профиля Telegram одним касанием.
 * Метод появился не во всех клиентах — если его нет, поле заполняется вручную.
 */
export function requestPhone(): Promise<string | null> {
  return new Promise((resolve) => {
    if (!real?.requestContact) return resolve(null);
    try {
      real.requestContact((ok, result) => {
        const phone = result?.responseUnsafe?.contact?.phone_number;
        resolve(ok && phone ? phone : null);
      });
    } catch {
      resolve(null);
    }
  });
}

export function setClosingConfirmation(on: boolean): void {
  if (!real) return;
  try {
    if (on) real.enableClosingConfirmation();
    else real.disableClosingConfirmation();
  } catch {
    /* не поддерживается — не критично */
  }
}
